import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import {
  listNews,
  queryByCollection,
  resetNewsPortal,
} from 'volto-ministerio-publico-federal/actions';

type Content = Record<string, unknown>;

/** `v` é lista em `selection.any` e `date.between`. */
export interface QueryItem {
  i: string;
  o: string;
  v: string | string[];
}

interface NewsState {
  result: {
    items?: { '@id': string }[];
    items_total?: number;
    batching?: unknown;
  };
  loading: boolean;
}

/**
 * `pagination` numera as páginas na URL (`b_start`); `loadMore` empilha lotes
 * na mesma tela, para o botão "Mostrar mais" e a rolagem infinita.
 */
export type NewsListingMode = 'pagination' | 'loadMore';

interface UseNewsListingOptions {
  query?: QueryItem[];
  sortOrder?: string;
  mode?: NewsListingMode;
}

interface UseNewsListingResult {
  result: NewsState['result'];
  loading: boolean;
  isCollection: boolean;
  effectiveCurrentPage: number;
  effectivePageSize: number;
  totalPages: number;
  handleQueryPaginationChange: (
    _e: React.SyntheticEvent | null,
    { activePage }: { activePage: number },
  ) => void;
  /** Só em `loadMore`: ainda há lote a carregar. */
  hasMore: boolean;
  /** Só em `loadMore`: um lote novo está a caminho, com a lista já na tela. */
  loadingMore: boolean;
  loadMore: () => void;
}

const defaultPageSize = '10';

/**
 * Paginação sempre refletida na URL via `b_start` (padrão Plone), nunca só
 * em estado local — URL compartilhável/atualizável precisa apontar pra
 * mesma página, e F5 não pode voltar pro início da listagem.
 *
 * O modo `loadMore` é a exceção deliberada: lá o que a URL guarda é o recorte
 * (os filtros), não quantos lotes o leitor já pediu. Recarregar volta ao
 * primeiro lote, e é isso que se espera de uma lista que cresce ao rolar.
 */
export const useNewsListing = (
  content: Content,
  options: UseNewsListingOptions = {},
): UseNewsListingResult => {
  const { query = [], sortOrder = 'descending', mode = 'pagination' } = options;
  const isLoadMore = mode === 'loadMore';

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const lastCommandRef = useRef<string>('');
  const requestedPathnameRef = useRef<string | null>(null);

  const { result, loading } = useSelector(
    (state: { listNewsPortal: NewsState }) => state.listNewsPortal,
  );

  const isCollection = Array.isArray(content?.query);

  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const urlBStart = parseInt(queryParams.get('b_start') || '0', 10);
  const effectivePageSize = isCollection
    ? Number(content?.item_count) || 10
    : Number(defaultPageSize);

  const [loadedPages, setLoadedPages] = useState(1);

  // Recorte pedido, sem a página: é o que precisa zerar os lotes acumulados.
  const filtersKey = useMemo(
    () =>
      isCollection
        ? `collection-${location.pathname}`
        : `list-${location.pathname}-${sortOrder}-${JSON.stringify(query)}`,
    [isCollection, location.pathname, sortOrder, query],
  );
  const filtersKeyRef = useRef(filtersKey);

  // Ajuste em tempo de render (padrão recomendado do React para estado
  // derivado): num `useEffect` o recorte novo chegaria a disparar uma busca
  // pela última página do recorte ANTERIOR antes do reset.
  if (isLoadMore && filtersKeyRef.current !== filtersKey) {
    filtersKeyRef.current = filtersKey;
    setLoadedPages(1);
  }

  const effectiveBStart = isLoadMore
    ? (loadedPages - 1) * effectivePageSize
    : urlBStart;
  const effectiveCurrentPage = isLoadMore
    ? loadedPages
    : Math.floor(urlBStart / effectivePageSize) + 1;
  const append = isLoadMore && loadedPages > 1;

  useEffect(() => {
    dispatch(resetNewsPortal());
    lastCommandRef.current = '';
  }, [location.pathname, dispatch]);

  useEffect(() => {
    const commandKey = isCollection
      ? `collection-${location.pathname}-${JSON.stringify({ b_start: effectiveBStart })}`
      : `list-${location.pathname}-${effectiveBStart}-${sortOrder}-${JSON.stringify(query)}`;

    if (commandKey === lastCommandRef.current) {
      return;
    }

    lastCommandRef.current = commandKey;

    if (isCollection) {
      dispatch(
        queryByCollection(content, { b_start: effectiveBStart }, { append }),
      );
    } else {
      dispatch(
        listNews(
          {
            portal_type: ['News', 'News Item'],
            b_size: defaultPageSize,
            b_start: `${effectiveBStart}`,
            sort_on: 'effective',
            sort_order: sortOrder,
            query,
          },
          '',
          { append },
        ),
      );
    }

    requestedPathnameRef.current = location.pathname;
  }, [
    dispatch,
    sortOrder,
    query,
    isCollection,
    content,
    effectiveBStart,
    append,
    location.pathname,
  ]);

  const handleQueryPaginationChange = useCallback(
    (
      _e: React.SyntheticEvent | null,
      { activePage }: { activePage: number },
    ) => {
      window.scrollTo(0, 0);
      const newStart = (activePage - 1) * effectivePageSize;
      const params = new URLSearchParams(location.search);
      if (newStart > 0) {
        params.set('b_start', newStart.toString());
      } else {
        params.delete('b_start');
      }
      history.push({ search: params.toString() });
    },
    [effectivePageSize, location.search, history],
  );

  const totalPages = useMemo(
    () => Math.ceil((result?.items_total ?? 0) / effectivePageSize),
    [result?.items_total, effectivePageSize],
  );

  const isPendingForRoute = requestedPathnameRef.current !== location.pathname;

  // Enquanto o lote novo não chega, a lista atual continua na tela — quem
  // sinaliza a espera é o próprio botão, via `loadingMore`.
  const loadingMore = isLoadMore && (loading || isPendingForRoute) && append;

  const hasMore =
    isLoadMore &&
    (result?.items?.length ?? 0) > 0 &&
    (result?.items?.length ?? 0) < (result?.items_total ?? 0);

  const loadMore = useCallback(() => {
    setLoadedPages((current) => current + 1);
  }, []);

  return {
    result,
    loading: (loading || isPendingForRoute) && !loadingMore,
    isCollection,
    effectiveCurrentPage,
    effectivePageSize,
    totalPages,
    handleQueryPaginationChange,
    hasMore,
    loadingMore,
    loadMore,
  };
};
